import React, { useState } from 'react';
import { NurtureSequence } from '@/types';
import { mockNurtureSequences } from '@/data/mockData';
import { cn, getChannelIcon, formatPercentage } from '@/utils';

interface SequenceEditorProps {
  selectedChannel: string;
}

export const SequenceEditor: React.FC<SequenceEditorProps> = ({ selectedChannel: _selectedChannel }) => {
  const [selectedSequence, setSelectedSequence] = useState<NurtureSequence | null>(
    mockNurtureSequences[0]
  );
  // const [selectedTouchpoint, setSelectedTouchpoint] = useState<TouchPoint | null>(null);

  if (!selectedSequence) {
    return (
      <div className="p-6">
        <div className="text-center text-slate-400 py-12">
          Select a sequence to edit
        </div>
      </div>
    );
  }

  const sequenceLibrary = mockNurtureSequences;

  return (
    <div className="flex h-full">
      {/* Sequence Library Panel */}
      <div className="w-80 bg-slate-800 border-r border-slate-600 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-50">
            Sequence Library
          </h2>
          <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-500">
            +
          </button>
        </div>

        <div className="space-y-3">
          {sequenceLibrary.map((sequence) => (
            <div 
              key={sequence.id}
              className={cn(
                'p-3 rounded-lg border cursor-pointer transition-colors',
                selectedSequence?.id === sequence.id
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-slate-600 hover:border-slate-500'
              )}
              onClick={() => setSelectedSequence(sequence)}
            >
              <div className="flex items-center space-x-2 mb-2">
                <span>{sequence.type === 'NEW_LEAD' ? '📧' : sequence.type === 'STALLED_APP' ? '🔄' : sequence.type === 'DROPPED_LEAD' ? '💔' : '🏠'}</span>
                <span className="text-sm font-medium text-slate-200">
                  {sequence.name}
                </span>
              </div>
              
              <div className="text-xs text-slate-400 space-y-1">
                {sequence.touchpoints.slice(0, 3).map((tp) => (
                  <div key={tp.id} className="flex items-center space-x-2">
                    <span>├─</span>
                    <span>{getChannelIcon(tp.type)}</span>
                    <span>{tp.name}</span>
                  </div>
                ))}
                {sequence.touchpoints.length > 3 && (
                  <div className="text-slate-500">
                    └─ +{sequence.touchpoints.length - 3} more
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-3 bg-slate-700 rounded-lg">
          <h3 className="text-sm font-medium text-slate-300 mb-2">
            📁 Templates Library
          </h3>
          <div className="text-xs text-slate-400">
            Click to expand template collection
          </div>
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-50">
              {selectedSequence.name}
            </h1>
            <p className="text-slate-400">
              {selectedSequence.description}
            </p>
          </div>
          <div className="flex space-x-3">
            <button className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-500">
              Save
            </button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500">
              Preview
            </button>
          </div>
        </div>

        {/* Timeline View */}
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-50 mb-4">
            Timeline View
          </h3>
          
          {/* Timeline visualization */}
          <div className="relative">
            <div className="flex items-center space-x-8 overflow-x-auto pb-4">
              {selectedSequence.touchpoints.map((tp, index) => (
                <div key={tp.id} className="flex flex-col items-center min-w-32">
                  {/* Timeline dot and line */}
                  <div className="flex items-center w-full">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>
                    {index < selectedSequence.touchpoints.length - 1 && (
                      <div className="h-px bg-slate-600 flex-1 ml-4" />
                    )}
                  </div>
                  
                  {/* Touchpoint info */}
                  <div className="text-center mt-2">
                    <div className="text-2xl mb-1">
                      {getChannelIcon(tp.type)}
                    </div>
                    <div className="text-sm font-medium text-slate-300">
                      {tp.name}
                    </div>
                    <div className="text-xs text-slate-400">
                      {tp.delay < 60 ? `${tp.delay} min` : 
                       tp.delay < 1440 ? `${Math.floor(tp.delay / 60)} hr` :
                       `${Math.floor(tp.delay / 1440)} days`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Touchpoint Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {selectedSequence.touchpoints.map((tp) => (
            <div 
              key={tp.id}
              className="bg-slate-800 border border-slate-600 rounded-lg p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-slate-50">
                  {getChannelIcon(tp.type)} {tp.name}
                </h4>
                <div className="flex space-x-2">
                  <button className="text-sm bg-slate-600 text-slate-300 px-3 py-1 rounded hover:bg-slate-500">
                    Edit
                  </button>
                  <button className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-500">
                    Test
                  </button>
                  <button className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-500">
                    Delete
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {tp.subject && (
                  <div>
                    <label className="text-sm text-slate-400">Subject:</label>
                    <div className="text-slate-300 bg-slate-700 p-2 rounded text-sm">
                      {tp.subject}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm text-slate-400">Content Preview:</label>
                  <div className="text-slate-300 bg-slate-700 p-2 rounded text-sm">
                    {tp.contentPreview}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400">Channel:</label>
                    <div className="text-slate-300 capitalize">
                      {tp.type}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Trigger:</label>
                    <div className="text-slate-300">
                      {tp.delay === 0 ? 'IMMEDIATE' : 
                       tp.delay < 60 ? `${tp.delay} minutes` :
                       `${Math.floor(tp.delay / 60)} hours`}
                    </div>
                  </div>
                </div>

                {tp.personalization && (
                  <div>
                    <label className="text-sm text-slate-400">Personalization Variables:</label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tp.personalization.map((variable) => (
                        <span 
                          key={variable}
                          className="bg-blue-600/20 text-blue-300 px-2 py-1 rounded text-xs"
                        >
                          {variable}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {tp.abTestVariant && (
                  <div className="border-t border-slate-600 pt-3">
                    <label className="text-sm text-slate-400">A/B Test Results:</label>
                    <div className="mt-2 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300">Open Rate:</span>
                        <span className="text-slate-300">
                          {formatPercentage((tp.abTestVariant.metrics.opened / tp.abTestVariant.metrics.sent) * 100)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300">Click Rate:</span>
                        <span className="text-slate-300">
                          {formatPercentage((tp.abTestVariant.metrics.clicked / tp.abTestVariant.metrics.sent) * 100)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};